import React, { useEffect, useState } from "react";
import { PortalService } from "services/portal";

const CARD = { background: "var(--bg-card)", borderRadius: 12, border: "1px solid var(--border)", padding: "24px" };
const PRIORITY_COLORS = { low: "#94a3b8", medium: "#f59e0b", high: "#ef4444" };
const MODULE_LABELS = {
  finance: "Análisis Financiero",
  tax: "Tributario",
  dte: "Facturación",
  payroll: "Remuneraciones",
  docs: "Documentos",
  general: "General",
};

function fmtDate(d) {
  return new Date(d).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" });
}

function RecCard({ rec, expanded, onToggle, onRead }) {
  const color = PRIORITY_COLORS[rec.priority] || "#64748b";
  const isUnread = !rec.readByClient;
  return (
    <div
      style={{
        ...CARD,
        borderLeft: `4px solid ${color}`,
        background: isUnread ? "#fffbeb" : "#fff",
        marginBottom: 12,
        cursor: "pointer",
      }}
      onClick={() => {
        onToggle(rec._id);
        if (isUnread) onRead(rec._id);
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {rec.priority}
            </span>
            <span style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 500 }}>
              · {MODULE_LABELS[rec.module] || rec.module}
            </span>
            {isUnread && (
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--negative)", marginLeft: 4, background: "var(--negative-bg)", padding: "2px 6px", borderRadius: 4 }}>
                NUEVO
              </span>
            )}
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>{rec.title}</h3>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p style={{ fontSize: 11, color: "var(--text-muted)" }}>{fmtDate(rec.publishedAt || rec.createdAt)}</p>
          {rec.createdBy?.name && <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>por {rec.createdBy.name}</p>}
        </div>
      </div>
      <p
        style={{
          fontSize: 14,
          color: "var(--text-secondary)",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          ...(expanded ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }),
        }}
      >
        {rec.body}
      </p>
      {!expanded && rec.body.length > 140 && (
        <p style={{ fontSize: 12, color: "#3b82f6", marginTop: 6 }}>Click para expandir</p>
      )}
    </div>
  );
}

export function PortalRecomendaciones() {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread
  const [expanded, setExpanded] = useState({});

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const res = await PortalService.listRecommendations({ unread: f === "unread", limit: 100 });
      setItems(res.items || []);
      setUnreadCount(res.unreadCount || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load("all"); }, []);

  const handleFilter = (f) => {
    setFilter(f);
    load(f);
  };

  const handleRead = async (id) => {
    try {
      await PortalService.markRecommendationRead(id);
      setItems((prev) => prev.map((r) => (r._id === id ? { ...r, readByClient: true } : r)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) { console.error(e); }
  };

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Recomendaciones</h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>
          Insights y oportunidades detectadas por tu contador · {unreadCount} sin leer
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => handleFilter("all")}
          style={{ padding: "6px 14px", fontSize: 13, border: "1px solid", borderColor: filter === "all" ? "#3b82f6" : "#cbd5e1", background: filter === "all" ? "#3b82f6" : "#fff", color: filter === "all" ? "var(--bg-card)" : "#475569", borderRadius: 6, cursor: "pointer" }}
        >
          Todas
        </button>
        <button
          onClick={() => handleFilter("unread")}
          style={{ padding: "6px 14px", fontSize: 13, border: "1px solid", borderColor: filter === "unread" ? "#3b82f6" : "#cbd5e1", background: filter === "unread" ? "#3b82f6" : "#fff", color: filter === "unread" ? "var(--bg-card)" : "#475569", borderRadius: 6, cursor: "pointer" }}
        >
          Sin leer ({unreadCount})
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-secondary)" }}>Cargando...</div>
      ) : items.length === 0 ? (
        <div style={{ ...CARD, textAlign: "center", color: "var(--text-muted)", padding: 40 }}>
          {filter === "unread" ? "No tienes recomendaciones sin leer" : "Tu contador no ha publicado recomendaciones aún"}
        </div>
      ) : (
        items.map((r) => (
          <RecCard key={r._id} rec={r} expanded={expanded[r._id]} onToggle={toggleExpand} onRead={handleRead} />
        ))
      )}
    </div>
  );
}
